/**
 * BrandSEO — runtime <head> branding per domain.
 *
 * Sets document.title, meta description, OG/Twitter tags, favicon and
 * theme-color from the active brand's `seo` block. index.html ships the
 * Homesbyli defaults (crawler fallback); this swaps them the moment the
 * SPA mounts on ucwonder.com.
 *
 * Known limitation: social scrapers that don't execute JS will read the
 * static index.html tags. Proper per-domain OG for link previews needs an
 * edge rewrite / prerender step at deploy time.
 */
import { useEffect } from 'react';
import { useBrand } from './useBrand';

const setMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  if (!href) return;
  document.head.querySelectorAll(`link[rel="${rel}"]`).forEach((el) => el.setAttribute('href', href));
};

export const BrandSEO = () => {
  const { brand } = useBrand();

  useEffect(() => {
    const seo = brand?.seo;
    if (!seo) return;
    document.title = seo.title;
    setMeta('name', 'description', seo.description);
    setMeta('name', 'theme-color', seo.themeColor);
    setMeta('property', 'og:title', seo.title);
    setMeta('property', 'og:description', seo.description);
    setMeta('property', 'og:site_name', brand.displayName);
    setMeta('property', 'og:url', `${seo.canonicalOrigin}${window.location.pathname}`);
    setMeta('property', 'og:image', seo.ogImage);
    setMeta('name', 'twitter:title', seo.title);
    setMeta('name', 'twitter:description', seo.description);
    setMeta('name', 'twitter:image', seo.ogImage);
    setLink('icon', seo.favicon);
    setLink('apple-touch-icon', seo.favicon);
  }, [brand]);

  return null;
};

export default BrandSEO;
